/**
 * THE WORDS HER PAY PAGE AND ITS RETURN PAGE SAY THAT HOME SAYS TOO (ops #19, home's questions of
 * 2026-10-14). Home reads ops's contract and never ops's code, so `docs/seams/home.md` section 5 prints
 * each of these as this file holds it, and `api/test/home-contract-words.test.ts` fails the gate when
 * one changes here and not there. `screens/PublicPage.tsx` and `screens/PaymentReturn.tsx` draw them,
 * and say nothing of their own in their place.
 */
import type { SavedMethodType } from '@fk/shared';
import { dayOf, longDay, money, savedMethodNoun, savedMethodWords } from './format.ts';

/** What the four steps of "How do I use Zelle?" name: the push's `pay.zelle` and the amount owed. */
export interface ZelleStepsFor {
  readonly tag: string;
  readonly name: string;
  readonly amount: string;
  readonly memo: string | null;
}

/** "How do I use Zelle?": the four steps, in order, the last naming the memo only where there is one. */
export function zelleSteps({
  tag,
  name,
  amount,
  memo,
}: ZelleStepsFor): readonly [string, string, string, string] {
  return [
    "Open your bank's app and choose Zelle, or Send money.",
    `On Select recipient, paste ${tag} or scan the code.`,
    `Check that it says ${name}.`,
    `Send ${amount}${memo ? ` with the memo ${memo}` : ''}.`,
  ];
}

/** Beneath the four steps, ahead of the office's number, which the page makes a link to call. */
export const ZELLE_STUCK = 'Stuck? Call or text the office at';

/** A charge that paid her invoice, as her page and home's copy of it read one. */
export interface ChargedFor {
  readonly displayLabel: string;
  readonly methodType: SavedMethodType | null;
  readonly amountCents: number;
  readonly chargedAt: string;
  readonly receiptEmailed: boolean;
}

/**
 * WHAT HER PAGE SAYS ONCE AN AUTOMATIC CHARGE PAID IT (ops #54, item 1): it said Paid and $0.00 owed
 * and nothing more (the staging walk of 2026-09-23). Which of her saved methods was charged, in the
 * words it is read by and never an id, how much, on the day in Florida, and a receipt only where one
 * was emailed. A method with no words of its own beyond its kind is named by the kind alone.
 */
export function chargedWords(charged: ChargedFor): string {
  const noun = savedMethodNoun(charged.methodType);
  const hers = savedMethodWords(charged);
  const named = charged.methodType !== null && hers !== noun;
  const which =
    charged.methodType === null
      ? 'your saved payment method'
      : named
        ? `your saved ${noun}, ${hers}`
        : `your saved ${noun}`;
  const day = dayOf(charged.chargedAt);
  // "To your saved card, Visa ending 4242, on Thursday": the second comma only where a day follows.
  const when = day === null ? '' : `${named ? ',' : ''} on ${longDay(day)}`;
  const receipt = charged.receiptEmailed ? ' A receipt was emailed.' : '';
  return `Paid automatically: ${money(charged.amountCents)} was charged to ${which}${when}.${receipt}`;
}

/**
 * THE PAGE STRIPE SENDS HER BACK TO, line by line (`screens/PaymentReturn.tsx` says which lines follow
 * which address): thanks and what comes next once she paid, how to stop a method she saved, and, when
 * she left Stripe's page without paying, how to pay. `questions` goes ahead of the office's number,
 * which the page makes a link to call.
 */
export const RETURN_PAGE_WORDS = {
  paid: 'Thank you.',
  onItsWay: 'Your payment is on its way to Faithfully Kept.',
  bankTakesDays: 'A bank payment can take a few days to arrive.',
  confirmYourBank:
    'If Stripe asked you to confirm your bank account, your payment starts once you do. Look for an email from Stripe.',
  savedAccount:
    "You asked us to charge your bank account automatically. Once this payment has cleared, we will charge it for each clean's invoice after you tell us the clean went well. You can stop it at any time by calling or texting the office.",
  savedBox:
    "If you chose to save your payment details on Stripe's page, we will charge them for each clean's invoice after you tell us the clean went well. You can stop it at any time by calling or texting the office.",
  close: 'You can close this page.',
  notPaid: 'You have not paid yet.',
  leftStripe: "You left Stripe's page before paying, so no payment was made.",
  howToPay: 'To pay, open your invoice again from the message we sent you.',
  questions: 'Questions? Call or text the office at',
} as const;
